import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ZrTheme = 'light' | 'dark';
export type ZrDensity = 'compact' | 'comfortable' | 'spacious';
export type ZrRadiusPreset = 'default' | 'sharp' | 'rounded';

export interface ZrThemeSettings {
  theme: ZrTheme;
  density: ZrDensity;
  radius: ZrRadiusPreset;
}

export interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ZrTheme;
  defaultDensity?: ZrDensity;
  defaultRadius?: ZrRadiusPreset;
}

interface ThemeContextValue {
  settings: ZrThemeSettings;
  setTheme: (theme: ZrTheme) => void;
  setDensity: (density: ZrDensity) => void;
  setRadius: (radius: ZrRadiusPreset) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyThemeToDocument(settings: ZrThemeSettings): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', settings.theme);
  root.setAttribute('data-density', settings.density);
  if (settings.radius === 'default') {
    root.removeAttribute('data-radius');
  } else {
    root.setAttribute('data-radius', settings.radius);
  }
}

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  defaultDensity = 'compact',
  defaultRadius = 'default',
}: ThemeProviderProps) {
  const [settings, setSettings] = useState<ZrThemeSettings>({
    theme: defaultTheme,
    density: defaultDensity,
    radius: defaultRadius,
  });

  useEffect(() => {
    applyThemeToDocument(settings);
  }, [settings]);

  const setTheme = useCallback((theme: ZrTheme) => {
    setSettings((prev) => ({ ...prev, theme }));
  }, []);

  const setDensity = useCallback((density: ZrDensity) => {
    setSettings((prev) => ({ ...prev, density }));
  }, []);

  const setRadius = useCallback((radius: ZrRadiusPreset) => {
    setSettings((prev) => ({ ...prev, radius }));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ settings, setTheme, setDensity, setRadius }),
    [settings, setTheme, setDensity, setRadius],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeSettings(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useThemeSettings must be used within ThemeProvider');
  }
  return ctx;
}
